import { acfReady } from '../../utils/utils.js';

import { onMapClick } from './elements/popup.js';
import './index.css';

export default () => {
	const defaultMapProvider = 'pdok';
	const mapMaxZoom = 19;
	const leafletAttributionPrefix =
		'<a href="https://leafletjs.com" target="_blank" rel="noopener" class="leaflet-brand">Leaflet</a>';
	const MAP_CONTAINER_CSS = 'acf-map-container';
	const MAP_ELEMENT_CSS = 'acf-map-element';

	const leafletApi = 'L'; // Leaflet is global window object mapped under window.L
	const actionsAllowedPerMs = 1000; // Allowed to do an API request every 1 second
	const forceApiDelay = 500; // If exceeded, force a 500ms delay before doing the API request

	async function init() {
		// Step 1 - Wait for all ACF fields to be ready
		await acfReady();

		// Step 2 - For each of the map containers, create a map manager
		const mapContainers = document.querySelectorAll(
			`.${ MAP_CONTAINER_CSS }`
		);
		mapContainers.forEach( ( container ) => {
			const mapElement = container.querySelector(
				`.${ MAP_ELEMENT_CSS }`
			);
			if ( ! mapElement ) return;
			createLeafletMap( { container, mapElement } );
		} );
	}

	/* =========================================== */
	/* ==== Initial waiting and gathering data ==== */
	/* =========================================== */
	function getMapAttributeData( { container } ) {
		return {
			lat: parseFloat( container.dataset.lat ),
			lng: parseFloat( container.dataset.lng ),
			zoom: parseInt( container.dataset.zoom, 10 ),
			mapProvider: container.dataset.mapProvider || defaultMapProvider,
		};
	}

	function getMapLayers( { mapProvider = defaultMapProvider } ) {
		const providers = window.leafletTileLayers || {};
		const result =
			providers[ mapProvider ] || providers[ defaultMapProvider ];
		if ( ! result ) return [];
		return Array.isArray( result ) ? result : [ result ];
	}

	/* =========================================== */
	/* ============= Map Creation ================ */
	/* =========================================== */

	// Step 2 - Create the Leaflet map
	function createLeafletMap( { container, mapElement } ) {
		if ( ! window[ leafletApi ] ) return;
		// Step 3 - gather info from html attributes parent field
		const { lat, lng, zoom, mapProvider } = getMapAttributeData( {
			container,
		} );
		const allLayers = getMapLayers( { mapProvider } );
		const layers = window.participation?.mapSuggestionsMapLayersEnabled
			? allLayers
			: allLayers.slice( 0, 1 );

		// Step 4 - create map and set coordinates
		const map = window[ leafletApi ]
			.map( mapElement )
			.setView( [ lat, lng ], zoom );
		map.attributionControl.setPrefix( leafletAttributionPrefix );

		const tileLayers = layers.map( ( layer ) =>
			window[ leafletApi ].tileLayer( layer.url, {
				maxZoom: mapMaxZoom,
				attribution: layer.attribution || '',
			} )
		);

		if ( tileLayers.length > 0 ) {
			tileLayers[ 0 ].addTo( map );
		}

		if ( tileLayers.length > 1 ) {
			const baseLayers = {};
			layers.forEach( ( layer, i ) => {
				baseLayers[ layer.name ] = tileLayers[ i ];
			} );
			window[ leafletApi ].control.layers( baseLayers ).addTo( map );
		}

		const icon = window[ leafletApi ].divIcon( {
			className: 'leaflet-data-marker',
			iconAnchor: [ 20, 25 ],
			iconSize: [ 40, 50 ],
			popupAnchor: [ 1, -30 ],
			html: `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 640 640'><path stroke='rgb(255 255 255 / 75%)' stroke-width='20' stroke-linejoin='round'  d='M128 252.6C128 148.4 214 64 320 64C426 64 512 148.4 512 252.6C512 371.9 391.8 514.9 341.6 569.4C329.8 582.2 310.1 582.2 298.3 569.4C248.1 514.9 127.9 371.9 127.9 252.6zM320 320C355.3 320 384 291.3 384 256C384 220.7 355.3 192 320 192C284.7 192 256 220.7 256 256C256 291.3 284.7 320 320 320z'/></svg>`,
		} );
		const marker = window[ leafletApi ]
			.marker( [ lat, lng ], { icon } )
			.addTo( map );
		const popup = window[ leafletApi ].popup();

		const mapState = {
			addressFetchTimeoutId: null,
			lastAddressFetchTime: 0,
			lastApiDoc: null,
		};

		// Step 5 - Map click listener using the onMapClick function
		map.on( 'click', ( event ) =>
			onMapClick( {
				popup,
				event,
				map,
				marker,
				container,
				mapState,
				actionsAllowedPerMs,
				forceApiDelay,
			} )
		);

		// Step 5.1 - Save leafletApi objects to the mapElement
		mapElement.leafletMapObject = map;
		mapElement.leafletMapDefaultMarker = marker;
	}

	init();
};
